/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { calculateOrderTotalEarned } from './calculations.ts';

export interface DailyBucket {
  dayKey: string; // YYYY-MM-DD, local calendar day
  date: Date;
  salesTotal: number;
  orderCount: number;
  earnedTotal: number;
}

const dayKeyFor = (iso: string): string => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Buckets a list of orders (each with createdAt/totalPrice/financialStatus)
// into one entry per calendar day they were placed on. Refunded orders
// still count toward orderCount (they happened) but never toward
// salesTotal/earnedTotal - the same "never counts as kept revenue" rule
// calculateOrderTotalEarned itself uses.
export const bucketOrdersByDay = (orders: any[]): Map<string, DailyBucket> => {
  const buckets = new Map<string, DailyBucket>();

  orders.forEach((order) => {
    if (!order.createdAt) return;
    const dayKey = dayKeyFor(order.createdAt);
    const isRefunded = order.financialStatus?.toLowerCase() === 'refunded';

    const existing = buckets.get(dayKey) || {
      dayKey,
      date: new Date(order.createdAt),
      salesTotal: 0,
      orderCount: 0,
      earnedTotal: 0
    };

    existing.orderCount += 1;
    if (!isRefunded) {
      existing.salesTotal += parseFloat(order.totalPrice?.toString()) || 0;
      existing.earnedTotal += calculateOrderTotalEarned(order);
    }

    buckets.set(dayKey, existing);
  });

  return buckets;
};

// Fills in every missing day in [start, end] (inclusive, by local calendar
// day) with a zero-value bucket, so a chart's x-axis has no gaps on days
// with no sales.
export const fillDailyRange = (
  buckets: Map<string, DailyBucket>,
  start: Date,
  end: Date
): DailyBucket[] => {
  const result: DailyBucket[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  while (cursor <= last) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const d = String(cursor.getDate()).padStart(2, '0');
    const dayKey = `${y}-${m}-${d}`;

    result.push(
      buckets.get(dayKey) || {
        dayKey,
        date: new Date(cursor),
        salesTotal: 0,
        orderCount: 0,
        earnedTotal: 0
      }
    );

    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
};

export interface WeeklyBucket {
  weekKey: string;
  weekStart: Date;
  salesTotal: number;
  orderCount: number;
  earnedTotal: number;
}

// Collapses daily buckets into Sunday-start weekly buckets, for a trend
// chart spanning a long "since last payment" window where one point per
// day would be too dense to read.
export const toWeeklyBuckets = (daily: DailyBucket[]): WeeklyBucket[] => {
  const weekly = new Map<string, WeeklyBucket>();

  daily.forEach((day) => {
    const weekStart = new Date(day.date);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const weekKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;

    const existing = weekly.get(weekKey) || {
      weekKey,
      weekStart,
      salesTotal: 0,
      orderCount: 0,
      earnedTotal: 0
    };

    existing.salesTotal += day.salesTotal;
    existing.orderCount += day.orderCount;
    existing.earnedTotal += day.earnedTotal;
    weekly.set(weekKey, existing);
  });

  return Array.from(weekly.values()).sort(
    (a, b) => a.weekStart.getTime() - b.weekStart.getTime()
  );
};
